import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Search, MoreHorizontal, Package, HardDrive, RefreshCcw, Pencil, Trash2, Layers, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';

const ASSET_STYLE = { available: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400', deployed: 'bg-blue-500/10 text-blue-700 dark:text-blue-400', in_repair: 'bg-amber-500/10 text-amber-700 dark:text-amber-400', retired: 'bg-muted text-muted-foreground' };
const RMA_STYLE = { pending: 'bg-amber-500/10 text-amber-700', shipped: 'bg-blue-500/10 text-blue-700', delivered: 'bg-emerald-500/10 text-emerald-700', returned: 'bg-violet-500/10 text-violet-700', cancelled: 'bg-muted text-muted-foreground' };
const STOCKING_LABELS = { new_deployment: 'New Deployment', replacement: 'Replacement', buffer: 'Buffer', repair_return: 'Repair Return', other: 'Other' };
const STOCKING_REASONS = ['new_deployment', 'replacement', 'buffer', 'repair_return', 'other'];
const ASSET_STATUSES = ['available', 'deployed', 'in_repair', 'retired'];
const RMA_STATUSES = ['pending', 'shipped', 'delivered', 'returned', 'cancelled'];

export default function InventoryPage() {
  const [products, setProducts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [rmaItems, setRmaItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [defects, setDefects] = useState([]);
  const [pSearch, setPSearch] = useState('');
  const [aSearch, setASearch] = useState('');
  const [aStatus, setAStatus] = useState('');
  const [rmaStatus, setRmaStatus] = useState('');
  const [selectedAssets, setSelectedAssets] = useState(new Set());
  // Product dialogs
  const [pDlg, setPDlg] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [pForm, setPForm] = useState({ name: '', sku: '', description: '', category: '', unit_price: '', stock_level: '', low_stock_threshold: '10' });
  // Asset dialogs
  const [aDlg, setADlg] = useState(false);
  const [bulkDlg, setBulkDlg] = useState(false);
  const [editAsset, setEditAsset] = useState(null);
  const [aForm, setAForm] = useState({ product_id: '', serial_number: '', status: 'available', assigned_to_customer: '', stocking_reason: '' });
  const [bulkForm, setBulkForm] = useState({ product_id: '', quantity: '5', stocking_reason: 'new_deployment' });
  // RMA dialog
  const [rmaDlg, setRmaDlg] = useState(false);
  const [rmaForm, setRmaForm] = useState({ replacement_asset_id: '', serial_number: '', linked_defect_id: '', original_asset_id: '', date_shipped: '', status: 'pending', notes: '' });

  const loadProducts = useCallback(() => { api.get('/products', { params: { search: pSearch } }).then(r => setProducts(r.data.products || [])); }, [pSearch]);
  const loadAssets = useCallback(() => { api.get('/assets', { params: { search: aSearch, status: aStatus } }).then(r => setAssets(r.data.assets || [])); }, [aSearch, aStatus]);
  const loadRMA = useCallback(() => { api.get('/rma', { params: { status: rmaStatus } }).then(r => setRmaItems(r.data.items || [])); }, [rmaStatus]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadAssets(); }, [loadAssets]);
  useEffect(() => { loadRMA(); }, [loadRMA]);
  useEffect(() => {
    api.get('/customers').then(r => setCustomers(r.data.customers || []));
    api.get('/defects').then(r => setDefects(r.data.defects || []));
  }, []);

  const getProductName = (id) => products.find(p => p.product_id === id)?.name || id || '-';

  // ─── Products ───
  const resetPForm = () => setPForm({ name: '', sku: '', description: '', category: '', unit_price: '', stock_level: '', low_stock_threshold: '10' });
  const openEditProduct = (p) => { setEditProduct(p); setPForm({ name: p.name, sku: p.sku || '', description: p.description || '', category: p.category || '', unit_price: String(p.unit_price || ''), stock_level: String(p.stock_level || ''), low_stock_threshold: String(p.low_stock_threshold || 10) }); setPDlg(true); };
  const openCreateProduct = () => { setEditProduct(null); resetPForm(); setPDlg(true); };

  const saveProduct = async () => {
    const payload = { ...pForm, unit_price: parseFloat(pForm.unit_price) || 0, stock_level: parseInt(pForm.stock_level) || 0, low_stock_threshold: parseInt(pForm.low_stock_threshold) || 10 };
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct.product_id}`, payload);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product created');
      }
      setPDlg(false); setEditProduct(null); resetPForm(); loadProducts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteProduct = async (pid) => {
    try {
      await api.delete(`/products/${pid}`);
      toast.success('Product deleted'); loadProducts();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nForce delete anyway?`)) {
          await api.delete(`/products/${pid}?force=true`);
          toast.success('Product force-deleted'); loadProducts();
        }
      } else { toast.error(e.response?.data?.detail || 'Failed'); }
    }
  };

  // ─── Assets ───
  const resetAForm = () => setAForm({ product_id: '', serial_number: '', status: 'available', assigned_to_customer: '', stocking_reason: '' });
  const openEditAsset = (a) => { setEditAsset(a); setAForm({ product_id: a.product_id, serial_number: a.serial_number || '', status: a.status, assigned_to_customer: a.assigned_to_customer || '', stocking_reason: a.stocking_reason || '' }); setADlg(true); };
  const openCreateAsset = () => { setEditAsset(null); resetAForm(); setADlg(true); };

  const saveAsset = async () => {
    try {
      if (editAsset) {
        await api.put(`/assets/${editAsset.asset_id}`, aForm);
        toast.success('Asset updated');
      } else {
        await api.post('/assets', aForm);
        toast.success('Asset created');
      }
      setADlg(false); setEditAsset(null); resetAForm(); loadAssets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const bulkCreate = async () => {
    try {
      const r = await api.post('/assets/bulk', { ...bulkForm, quantity: parseInt(bulkForm.quantity) || 1 });
      toast.success(r.data.message);
      setBulkDlg(false); setBulkForm({ product_id: '', quantity: '5', stocking_reason: 'new_deployment' }); loadAssets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const toggleAssetSelect = (id) => setSelectedAssets(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const toggleSelectAll = () => { if (selectedAssets.size === assets.length) setSelectedAssets(new Set()); else setSelectedAssets(new Set(assets.map(a => a.asset_id))); };

  const bulkStatusUpdate = async (status) => {
    if (selectedAssets.size === 0) return;
    try {
      const r = await api.put('/assets/bulk-status', { asset_ids: Array.from(selectedAssets), status });
      toast.success(r.data.message); setSelectedAssets(new Set()); loadAssets();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ─── RMA ───
  const resetRmaForm = () => setRmaForm({ replacement_asset_id: '', serial_number: '', linked_defect_id: '', original_asset_id: '', date_shipped: '', status: 'pending', notes: '' });
  const createRMA = async () => {
    try {
      await api.post('/rma', rmaForm);
      toast.success('RMA created'); setRmaDlg(false); resetRmaForm(); loadRMA();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const updateRMAStatus = async (id, status) => { await api.put(`/rma/${id}`, { status }); toast.success('Updated'); loadRMA(); };
  const deleteRMA = async (id) => { await api.delete(`/rma/${id}`); toast.success('Deleted'); loadRMA(); };

  return (
    <div data-testid="inventory-page" className="space-y-4">
      <h1 className="font-heading text-2xl font-bold tracking-tight">Inventory</h1>
      <Tabs defaultValue="products">
        <TabsList>
          <TabsTrigger value="products" data-testid="tab-products"><Package size={14} className="mr-1" /> Products</TabsTrigger>
          <TabsTrigger value="assets" data-testid="tab-assets"><HardDrive size={14} className="mr-1" /> Assets</TabsTrigger>
          <TabsTrigger value="rma" data-testid="tab-rma"><RefreshCcw size={14} className="mr-1" /> RMA</TabsTrigger>
        </TabsList>

        {/* ═══ PRODUCTS TAB ═══ */}
        <TabsContent value="products" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} /><Input placeholder="Search products..." value={pSearch} onChange={e => setPSearch(e.target.value)} className="pl-10 w-64" data-testid="product-search" /></div>
            <Button onClick={openCreateProduct} data-testid="create-product-btn"><Plus size={16} className="mr-1" /> New Product</Button>
          </div>
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Name</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead><TableHead>Price</TableHead><TableHead>Stock</TableHead><TableHead>Threshold</TableHead><TableHead className="w-[60px]" />
              </TableRow></TableHeader>
              <TableBody>
                {products.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No products</TableCell></TableRow>}
                {products.map(p => (
                  <TableRow key={p.product_id} className="hover:bg-muted/50" data-testid={`product-row-${p.product_id}`}>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="font-mono text-xs">{p.sku || '-'}</TableCell>
                    <TableCell className="text-sm">{p.category || '-'}</TableCell>
                    <TableCell>${p.unit_price?.toLocaleString()}</TableCell>
                    <TableCell><span className={p.stock_level <= p.low_stock_threshold ? 'text-destructive font-semibold' : ''}>{p.stock_level}</span></TableCell>
                    <TableCell className="text-muted-foreground">{p.low_stock_threshold}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditProduct(p)}><Pencil size={12} className="mr-1.5" /> Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => deleteProduct(p.product_id)}><Trash2 size={12} className="mr-1.5" /> Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ═══ ASSETS TAB ═══ */}
        <TabsContent value="assets" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} /><Input placeholder="Search serial/ID..." value={aSearch} onChange={e => setASearch(e.target.value)} className="pl-10 w-56" data-testid="asset-search" /></div>
              <Select value={aStatus || 'all'} onValueChange={v => setAStatus(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-36" data-testid="asset-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent><SelectItem value="all">All Status</SelectItem>{ASSET_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulkDlg(true)} data-testid="bulk-create-btn"><Layers size={14} className="mr-1" /> Bulk Create</Button>
              <Button onClick={openCreateAsset} data-testid="create-asset-btn"><Plus size={16} className="mr-1" /> New Asset</Button>
            </div>
          </div>

          {selectedAssets.size > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-primary/5 border border-primary/20" data-testid="bulk-action-bar">
              <CheckSquare size={16} className="text-primary" />
              <span className="text-sm font-medium">{selectedAssets.size} selected</span>
              <div className="flex gap-2 ml-auto">
                {ASSET_STATUSES.map(s => (
                  <Button key={s} size="sm" variant="outline" onClick={() => bulkStatusUpdate(s)} data-testid={`bulk-set-${s}`}>
                    Set {s.replace('_', ' ')}
                  </Button>
                ))}
                <Button size="sm" variant="ghost" onClick={() => setSelectedAssets(new Set())}>Clear</Button>
              </div>
            </div>
          )}

          <div className="rounded-md border border-border">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="w-10"><Checkbox checked={assets.length > 0 && selectedAssets.size === assets.length} onCheckedChange={toggleSelectAll} data-testid="asset-select-all" /></TableHead>
                <TableHead>ID / Serial</TableHead><TableHead>Product</TableHead><TableHead>Status</TableHead><TableHead>Stocking Reason</TableHead><TableHead>Customer</TableHead><TableHead className="w-[50px]" />
              </TableRow></TableHeader>
              <TableBody>
                {assets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No assets</TableCell></TableRow>}
                {assets.map(a => (
                  <TableRow key={a.asset_id} className={`hover:bg-muted/50 ${selectedAssets.has(a.asset_id) ? 'bg-primary/5' : ''}`} data-testid={`asset-row-${a.asset_id}`}>
                    <TableCell><Checkbox checked={selectedAssets.has(a.asset_id)} onCheckedChange={() => toggleAssetSelect(a.asset_id)} /></TableCell>
                    <TableCell className="font-mono text-xs">{a.serial_number || <span className="text-muted-foreground italic">{a.asset_id.slice(0, 16)}</span>}</TableCell>
                    <TableCell className="text-sm">{getProductName(a.product_id)}</TableCell>
                    <TableCell><Badge variant="secondary" className={`text-xs ${ASSET_STYLE[a.status]}`}>{a.status?.replace('_', ' ')}</Badge></TableCell>
                    <TableCell className="text-xs">{STOCKING_LABELS[a.stocking_reason] || a.stocking_reason || '-'}</TableCell>
                    <TableCell className="text-xs">{customers.find(c => c.customer_id === a.assigned_to_customer)?.name || '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditAsset(a)}><Pencil size={12} className="mr-1.5" /> Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {ASSET_STATUSES.filter(s => s !== a.status).map(s => (
                            <DropdownMenuItem key={s} onClick={() => api.put(`/assets/${a.asset_id}`, { status: s }).then(() => { toast.success('Updated'); loadAssets(); })}>Set {s.replace('_', ' ')}</DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ═══ RMA TAB ═══ */}
        <TabsContent value="rma" className="mt-4 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Select value={rmaStatus || 'all'} onValueChange={v => setRmaStatus(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-36" data-testid="rma-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem>{RMA_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => { resetRmaForm(); setRmaDlg(true); }} data-testid="create-rma-btn"><Plus size={16} className="mr-1" /> New RMA</Button>
          </div>
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>RMA ID</TableHead><TableHead>Replacement Asset</TableHead><TableHead>Serial #</TableHead><TableHead>Linked Defect</TableHead><TableHead>Original Asset</TableHead><TableHead>Shipped</TableHead><TableHead>Status</TableHead><TableHead className="w-[50px]" />
              </TableRow></TableHeader>
              <TableBody>
                {rmaItems.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No RMA records</TableCell></TableRow>}
                {rmaItems.map(r => (
                  <TableRow key={r.rma_id} className="hover:bg-muted/50" data-testid={`rma-row-${r.rma_id}`}>
                    <TableCell className="font-mono text-xs">{r.rma_id.slice(0, 16)}</TableCell>
                    <TableCell className="text-xs">{r.replacement_asset_id || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">{r.serial_number || '-'}</TableCell>
                    <TableCell className="text-xs">{r.linked_defect_id || '-'}</TableCell>
                    <TableCell className="text-xs">{r.original_asset_id || '-'}</TableCell>
                    <TableCell className="text-xs">{r.date_shipped || '-'}</TableCell>
                    <TableCell><Badge variant="secondary" className={`text-xs ${RMA_STYLE[r.status] || ''}`}>{r.status}</Badge></TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal size={14} /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {RMA_STATUSES.filter(s => s !== r.status).map(s => <DropdownMenuItem key={s} onClick={() => updateRMAStatus(r.rma_id, s)}>Set {s}</DropdownMenuItem>)}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => deleteRMA(r.rma_id)}>Delete</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* ─── Product Dialog ─── */}
      <Dialog open={pDlg} onOpenChange={v => { if (!v) setEditProduct(null); setPDlg(v); }}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editProduct ? 'Edit' : 'New'} Product</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name *</Label><Input value={pForm.name} onChange={e => setPForm(f => ({ ...f, name: e.target.value }))} data-testid="product-name-input" /></div>
              <div className="space-y-1.5"><Label>SKU</Label><Input value={pForm.sku} onChange={e => setPForm(f => ({ ...f, sku: e.target.value }))} data-testid="product-sku-input" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Price</Label><Input type="number" value={pForm.unit_price} onChange={e => setPForm(f => ({ ...f, unit_price: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Stock</Label><Input type="number" value={pForm.stock_level} onChange={e => setPForm(f => ({ ...f, stock_level: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Low Threshold</Label><Input type="number" value={pForm.low_stock_threshold} onChange={e => setPForm(f => ({ ...f, low_stock_threshold: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>Category</Label><Input value={pForm.category} onChange={e => setPForm(f => ({ ...f, category: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Description</Label><Textarea value={pForm.description} onChange={e => setPForm(f => ({ ...f, description: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPDlg(false)}>Cancel</Button><Button onClick={saveProduct} disabled={!pForm.name} data-testid="product-save-btn">{editProduct ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Asset Dialog (Create/Edit) ─── */}
      <Dialog open={aDlg} onOpenChange={v => { if (!v) setEditAsset(null); setADlg(v); }}>
        <DialogContent data-testid="asset-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editAsset ? 'Edit' : 'New'} Asset</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Product *</Label>
              <Select value={aForm.product_id} onValueChange={v => setAForm(f => ({ ...f, product_id: v }))} disabled={!!editAsset}>
                <SelectTrigger data-testid="asset-product-select"><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Serial Number</Label><Input value={aForm.serial_number} onChange={e => setAForm(f => ({ ...f, serial_number: e.target.value }))} placeholder="Enter or leave blank" data-testid="asset-serial-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={aForm.status} onValueChange={v => setAForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Stocking Reason</Label>
                <Select value={aForm.stocking_reason || 'none'} onValueChange={v => setAForm(f => ({ ...f, stocking_reason: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{STOCKING_REASONS.map(s => <SelectItem key={s} value={s}>{STOCKING_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Customer</Label>
              <Select value={aForm.assigned_to_customer || 'none'} onValueChange={v => setAForm(f => ({ ...f, assigned_to_customer: v === 'none' ? '' : v }))}>
                <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent><SelectItem value="none">None</SelectItem>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setADlg(false)}>Cancel</Button><Button onClick={saveAsset} disabled={!aForm.product_id} data-testid="asset-save-btn">{editAsset ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Bulk Asset Create Dialog ─── */}
      <Dialog open={bulkDlg} onOpenChange={setBulkDlg}>
        <DialogContent data-testid="bulk-asset-dialog">
          <DialogHeader><DialogTitle className="font-heading">Bulk Create Assets</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="space-y-1.5"><Label>Product *</Label>
              <Select value={bulkForm.product_id} onValueChange={v => setBulkForm(f => ({ ...f, product_id: v }))}>
                <SelectTrigger data-testid="bulk-product-select"><SelectValue placeholder="Select product..." /></SelectTrigger>
                <SelectContent>{products.map(p => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" min="1" max="500" value={bulkForm.quantity} onChange={e => setBulkForm(f => ({ ...f, quantity: e.target.value }))} data-testid="bulk-quantity-input" /></div>
              <div className="space-y-1.5"><Label>Stocking Reason *</Label>
                <Select value={bulkForm.stocking_reason} onValueChange={v => setBulkForm(f => ({ ...f, stocking_reason: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{STOCKING_REASONS.map(s => <SelectItem key={s} value={s}>{STOCKING_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Assets will be created without serial numbers. You can edit each asset later to add serial numbers.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBulkDlg(false)}>Cancel</Button><Button onClick={bulkCreate} disabled={!bulkForm.product_id} data-testid="bulk-create-submit">Create {bulkForm.quantity} Assets</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── RMA Dialog ─── */}
      <Dialog open={rmaDlg} onOpenChange={setRmaDlg}>
        <DialogContent className="sm:max-w-lg" data-testid="rma-dialog">
          <DialogHeader><DialogTitle className="font-heading">New RMA / Replacement</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Replacement Asset</Label>
                <Select value={rmaForm.replacement_asset_id || 'none'} onValueChange={v => setRmaForm(f => ({ ...f, replacement_asset_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{assets.filter(a => a.status === 'available').map(a => <SelectItem key={a.asset_id} value={a.asset_id}>{a.serial_number || a.asset_id.slice(0, 16)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Serial Number</Label><Input value={rmaForm.serial_number} onChange={e => setRmaForm(f => ({ ...f, serial_number: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Linked Defect</Label>
                <Select value={rmaForm.linked_defect_id || 'none'} onValueChange={v => setRmaForm(f => ({ ...f, linked_defect_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{defects.map(d => <SelectItem key={d.defect_id} value={d.defect_id}>{d.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Original Asset</Label>
                <Select value={rmaForm.original_asset_id || 'none'} onValueChange={v => setRmaForm(f => ({ ...f, original_asset_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{assets.map(a => <SelectItem key={a.asset_id} value={a.asset_id}>{a.serial_number || a.asset_id.slice(0, 16)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date Shipped</Label><Input type="date" value={rmaForm.date_shipped} onChange={e => setRmaForm(f => ({ ...f, date_shipped: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Status</Label>
                <Select value={rmaForm.status} onValueChange={v => setRmaForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{RMA_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea value={rmaForm.notes} onChange={e => setRmaForm(f => ({ ...f, notes: e.target.value }))} rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setRmaDlg(false)}>Cancel</Button><Button onClick={createRMA} data-testid="rma-create-submit">Create RMA</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
